"use client"
import styled from "@emotion/styled"
import { Button, CircularProgress, IconButton } from "@mui/material"
import { RefObject, useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import { JobLocationIcon, NewText, transitionDelayTime, TransitionUp, useIsLargeDesktop } from "ui"
import ImageWithAlt from "ui/ImageWithAlt"
import { sharedColors } from "ui/theme/colors"
import {
  darkenHex,
  getUmbracoMediaUrl,
  pageStripClick,
  ProjectPage,
  scheduleBmbyMeeting,
  verifyCtaColor,
} from "utils"
import PowerNumbers from "./PowerNumbers"
import TimerPopup from "./TimerPopup"

const MIN_HEIGHT = 815
const timingFunction = `cubic-bezier(0.25, 0.46, 0.45, 0.94)`
const ANIM_DURATION = "2000ms"
const ANIM_DURATION_MOBILE = "1200ms"
const ANIM_DURATION_LATER_TEXT = "1200ms"
const ANIM_DURATION_LATER_TEXT_MOBILE = "1200ms"
const SKEW_ANGLE = -10
const anim = ({
  delay,
  name,
  duration,
  fillMode,
}: {
  name?: string
  delay?: string
  duration?: string
  fillMode?: string
}) =>
  `${name ?? ""} ${duration ?? ANIM_DURATION} ${timingFunction} ${delay ?? "1ms"} ${
    fillMode ?? "forwards"
  }`

function useTextContentMargin(skewAngle: string | number, ref: RefObject<HTMLElement>): number {
  const [margin, setMargin] = useState(0)

  useEffect(() => {
    function calcMargin() {
      if (!ref.current) return

      const angle = typeof skewAngle === "number" ? skewAngle : parseFloat(skewAngle)
      const radians = (angle * Math.PI) / 180
      const heroHeight = ref.current.offsetHeight // ← use element height
      const tan = Math.tan(radians)
      const m = tan * heroHeight

      setMargin(Math.round(Math.abs(m)) + 1)
    }

    calcMargin()
    window.addEventListener("resize", calcMargin)
    return () => window.removeEventListener("resize", calcMargin)
  }, [skewAngle, ref])

  return margin
}

const HeroContentWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: calc(100vh - 64px);
  min-height: calc(100dvh - 64px);
  z-index: 1;
  overflow: visible;
  ${(props) => props.theme.breakpoints.up("lg")} {
    height: calc(100vh - 98px);
    height: calc(100dvh - 98px);
    height: max(calc(100vh - 98px), ${MIN_HEIGHT}px);
    height: max(calc(100dvh - 98px), ${MIN_HEIGHT}px);
    overflow: visible;
    min-height: unset;
    flex-direction: row;
    justify-content: flex-end;
  }
`
const HeroMediaContainer = styled.div<{ locale: string; ready: boolean }>`
  top: 0;
  height: calc((100dvh - 64px) / 2);
  width: 100%;
  flex-grow: 0;
  opacity: 1;
  flex-shrink: 0;
  display: flex;

  z-index: 1;
  overflow: hidden;
  position: relative;
  background-color: ${(props) => props.theme.colors.advantagesBg4rentMarketing};
  animation: ${anim({ name: "media-entrance-mobile", duration: ANIM_DURATION_MOBILE })};
  animation-play-state: ${(props) => (props.ready ? "running" : "paused")};
  will-change: width, height, opacity, transform;
  ${(props) => props.theme.breakpoints.up("lg")} {
    width: 100%;
    height: 100%;
    opacity: 1;

    align-self: stretch;
    flex-shrink: 0;
    position: relative;

    animation: ${anim({ name: "media-entrance", duration: ANIM_DURATION })};
    animation-play-state: ${(props) => (props.ready ? "running" : "paused")};
    video {
    }
  }
  @keyframes media-entrance-mobile {
    0% {
      height: 100dvh;
      opacity: 1;
    }
    100% {
      height: calc((100dvh - 64px) / 2);
      opacity: 1;
    }
  }
  @keyframes media-entrance {
    0% {
      width: 120%;
      /* opacity: 0; */
    }
    100% {
      width: 62%;
      /* opacity: 1; */
    }
  }
`

const HeroTextContentWrapper = styled.div<{
  locale: string
  ready: boolean
  textContentMargin?: number
}>`
  --text-content-margin: ${(props) =>
    props.textContentMargin ? props.textContentMargin + "px" : "0"};
  top: 0;
  z-index: 2;
  min-height: calc((100dvh - 64px) / 2);
  order: 1;
  display: flex;
  margin: 0;
  width: 100%;
  visibility: ${(props) => (props.ready ? "visible" : "hidden")};
  pointer-events: ${(props) => (props.ready ? "auto" : "none")};
  transform: translateX(200%) scale(0.98);
  opacity: 0;
  animation: ${anim({ name: "text-entrance-mobile", duration: ANIM_DURATION_MOBILE })};
  animation-play-state: ${(props) => (props.ready ? "running" : "paused")};
  will-change: transform, opacity;
  position: relative;
  top: -16px;

  ${(props) => props.theme.breakpoints.up("lg")} {
    top: unset;
    position: relative;
    flex-direction: column;
    align-items: center;
    width: 50%;
    order: 0;
    flex: 1;
    flex-shrink: 0;
    padding-inline: 0;
    /* padding-inline-start: 120px; */
    /* padding-inline-end: 10%; */
    height: 100%;
    align-self: stretch;

    align-items: flex-end;

    inset-inline-start: 0%;
    opacity: 1;
    transform: scale(1);

    animation: ${anim({ name: "text-entrance", duration: ANIM_DURATION })};
    animation-play-state: ${(props) => (props.ready ? "running" : "paused")};

    > * {
      margin-inline-end: -15px;
    }

    &::before {
      background-color: white;
      content: "";
      position: absolute;
      top: 0;
      inset-inline-start: ${(props) =>
        props.locale === "he" ? 0 : "calc(var(--text-content-margin) * -1 + 20px)"};
      display: flex;
      width: calc(100% + var(--text-content-margin));
      height: 100%;
      z-index: -1;
      transform: skew(${(props) => (props.locale === "he" ? SKEW_ANGLE : SKEW_ANGLE * -1)}deg);
      transform-origin: ${(props) => (props.locale === "he" ? "bottom left" : "top right")};
    }
    &::after {
      content: "";
      width: 195px;
      height: 205px;
      overflow: hidden;
      background-image: url("/icons/polygon.svg");
      background-image: ${(props) =>
        props.locale === "he" ? "url('/icons/polygon.svg')" : "url('/icons/polygon-flipped.svg')"};
      background-repeat: no-repeat;
      background-size: cover;
      background-position: 0px 130px;
      display: flex;
      position: absolute;
      z-index: -1;
      inset-block-start: calc(100% - 205px);
      inset-inline-start: 0;
    }
  }
  @keyframes text-entrance-mobile {
    0% {
      transform: translateX(200%) scale(0.98);
      opacity: 0;
    }
    100% {
      opacity: 1;
      transform: translateX(0) scale(1);
    }
  }
  @keyframes text-entrance {
    0% {
      inset-inline-start: 0%;
    }
    100% {
      inset-inline-start: 0;
    }
  }
`
const HeroTextContentWrapperContent = styled.div<{ locale: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-inline: 20px;
  margin-bottom: -70px;
  ${(props) => props.theme.breakpoints.up("lg")} {
    margin-bottom: unset;
    width: min(80%, 500px);
    padding-inline: 0;
    margin-block: 90px 0;
    position: relative;
    inset-inline-start: 40px;
  }
`
const StyledVideo = styled.video<{ ready: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: ${(props) => (props.ready ? 1 : 0)};
  visibility: ${(props) => (props.ready ? "visible" : "hidden")};
  transition: opacity 300ms ease;
  will-change: opacity;
`

const StyledImage = styled(ImageWithAlt)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const HeroTextTitle = styled.h1<{ locale: string }>`
  font-family: var(--narkis-condensed) !important;
  font-weight: 700;
  line-height: 1.45;
  margin: 0;
  margin-block: 0 16px;
  color: ${(props) => props.theme.colors.darkBlue4rentMarketing};
  font-size: 60px;
  letter-spacing: -1.2px;
  ${(props) => props.theme.breakpoints.up("lg")} {
    font-weight: 700;
    letter-spacing: -1.8px;
    font-size: 90px;
    margin-block: 0 28px;
    line-height: 1.05;
  }
  strong,
  span:not(.HeroTextTitle-default) {
    font-family: var(--narkis-condensed) !important;
    justify-content: center;
    align-items: center;
    line-height: 1.2;
    height: 64px;
    font-weight: 700;
    color: white;
    margin-inline-start: 0;
    font-size: 60px;
    padding-inline: 12px;
    position: relative;
    z-index: 1;
    display: inline-flex;
    /* letter-spacing: -1.2px; */
    letter-spacing: -2.4px;
    ${(props) => props.theme.breakpoints.up("lg")} {
      display: flex;
      height: 108px;
      width: 505px;
      font-size: 120px;
      padding-inline: 5px;
      margin-inline: 0;
      white-space: nowrap;
      letter-spacing: -2.4px;
    }
    /* clip-path: polygon(10% 0%, 100% 0%, 97% 100%, 0% 100%); */
    &::before {
      content: "";
      display: flex;
      width: 100%;
      height: 100%;
      position: absolute;
      z-index: -1;
      top: 0;
      inset-inline-end: 0;
      /* background color and skew */
      background-color: ${(props) => props.theme.colors.blue4rentMarketing};
      transform: skew(${(props) => (props.locale === "he" ? SKEW_ANGLE : SKEW_ANGLE * -1)}deg);
    }
  }
  .HeroTextTitle-default {
    ${(props) => props.theme.breakpoints.down("lg")} {
      position: relative;
      top: 10px;
    }
  }
`

const StyledTextIcon = styled.div`
  display: flex;
  width: 100%;
  gap: 4px;
  font-size: 24px;
  flex-shrink: 0;
  align-items: center;
  margin-block: 0 8px;
  color: ${(props) => props.theme.colors.darkBlue4rentMarketing} !important;
  ${(props) => props.theme.breakpoints.up("lg")} {
    font-size: 32px;
    line-height: 130%;
  }
`
const StyledIconButton = styled(IconButton)`
  margin-inline-start: -10px;
  padding: 0px;
  color: ${(props) => props.theme.colors.darkBlue4rentMarketing} !important;
  ${(props) => props.theme.breakpoints.up("lg")} {
    width: 32px;
    height: 32px;
  }
`

const HeroDescription = styled.div`
  font-weight: 300;
  font-size: 18px;
  line-height: 150%;
  letter-spacing: calc(1em * (-2 / 100));
  margin-block: 0 24px;
  color: ${({ theme }) => theme.colors.darkBlue4rentMarketing};
  ${(props) => props.theme.breakpoints.up("lg")} {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: clip;
    -webkit-line-clamp: 6;
    font-size: 24px;
    padding-inline-end: 40px;
  }
  > * {
    margin-block: 0;
  }
`

const HeroActionButtons = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-block: 0 40px;
  ${(props) => props.theme.breakpoints.up("lg")} {
    margin-block: 0;
  }
`

const HeroActionButton = styled(Button)`
  border-radius: 8px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0px;
  font-size: 18px;
  padding: 8px 16px;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("md")} {
    width: unset;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    font-size: 20px;
    padding: 12px 28px;
  }
`

const HeroActionButton1 = styled(HeroActionButton)<{ dynamicColor?: string }>`
  --theme-color: ${(props) => props.dynamicColor || props.theme.colors.blue4rentMarketing};
  color: white;
  background-color: var(--theme-color);
  border-color: var(--theme-color);

  &:hover {
    background-color: ${(props) =>
      props.dynamicColor
        ? darkenHex(props.dynamicColor)
        : props.theme.colors.blue4rentMarketingHover};
  }
`

const HeroActionButton2 = styled(HeroActionButton)`
  --theme-color: ${(props) => props.theme.colors.darkBlue4rentMarketing};
  border: 1px solid var(--theme-color);
  color: var(--theme-color);
  background-color: transparent;
  &:hover {
    background-color: var(--theme-color);
    color: white;
    border-width: 1px;
    border-color: ${(props) => props.theme.colors.darkBlue4rentMarketing};
  }
`

const getDynamicButton = (color: string, props: any, children?: React.ReactNode | string) => {
  switch (color) {
    case "#ffffff": {
      return (
        <HeroActionButton2 variant="outlined" {...props}>
          {children}
        </HeroActionButton2>
      )
    }
    default: {
      return (
        <HeroActionButton1 variant="contained" dynamicColor={color} {...props}>
          {children}
        </HeroActionButton1>
      )
    }
  }
}

const SlideFromSide = styled.div<{ ready: boolean }>`
  transform: translateX(200%) scale(0.98);
  opacity: 0;
  animation: ${anim({
    name: "slide-from-side-mobile",
    duration: ANIM_DURATION_LATER_TEXT_MOBILE,
    delay: ANIM_DURATION_MOBILE,
  })};
  animation-name: slide-from-side-mobile;
  animation-play-state: ${(props) => (props.ready ? "running" : "paused")};
  will-change: transform, opacity;
  ${(props) => props.theme.breakpoints.up("lg")} {
    animation: ${anim({
      name: "slide-from-side",
      duration: ANIM_DURATION_LATER_TEXT,
      delay: ANIM_DURATION,
    })};
    transform: translateX(-200%) scale(0.98);
    opacity: 0;
  }
  @keyframes slide-from-side-mobile {
    0% {
      transform: translateX(200%) scale(0.98);
      opacity: 0;
    }
    100% {
      transform: translateX(0) scale(1);
      opacity: 1;
    }
  }
  @keyframes slide-from-side {
    0% {
      transform: translateX(-200%) scale(0.98);
      opacity: 0;
    }
    100% {
      transform: translateX(0) scale(1);
      opacity: 1;
    }
  }
`

const StyledTransitionUp = styled(TransitionUp)<{ $inView: boolean; $delay?: number }>`
  position: relative;
  z-index: 1;
  min-height: calc(100vh - 64px);
  min-height: calc(100dvh - 64px);
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0;
  ${(props) => props.theme.breakpoints.up("lg")} {
    align-items: unset;
    height: max(calc(100vh - 98px), ${MIN_HEIGHT}px);
    height: max(calc(100dvh - 98px), ${MIN_HEIGHT}px);
    min-height: unset;
  }
`

const OverflowClip = styled.div`
  ${(props) => props.theme.breakpoints.down("lg")} {
    width: 100%;
    overflow-x: clip;
    overflow-y: visible;
    height: max-content;
  }
`

type Props = {
  locale: string
  projectData: ProjectPage
  city: string | undefined
  country: string | undefined
  powerNumbersEnabled?: boolean
}

const HeroContent = ({ locale, projectData, city, country, powerNumbersEnabled }: Props) => {
  const heroContentWrapperRef = useRef<HTMLDivElement>(null)
  const isDesktop = useIsLargeDesktop()
  const { ref, inView } = useInView({
    rootMargin: "0px",
    threshold: 0.1,
    initialInView: true,
    triggerOnce: true,
  })
  const { ref: powerNumbersRef, inView: powerNumbersInView } = useInView({
    rootMargin: `0px 0px -300px 0px`,
    threshold: 0.01,
  })
  const rentProjectFOMO = projectData?.rentProjectFOMO
  const powerNumbers = projectData?.numbersNew
  const shouldRenderPowerNumbers = powerNumbers && powerNumbers.length > 0 && powerNumbers[0].number
  const allowPowerNumbers = powerNumbersEnabled !== false

  const bannerData = projectData?.rentProjectHeroBanner

  let anchorButtonBlueColor = bannerData?.anchorButtonBlueColor
  if (!anchorButtonBlueColor?.startsWith("#")) {
    anchorButtonBlueColor = "#" + anchorButtonBlueColor
  }
  anchorButtonBlueColor = verifyCtaColor(anchorButtonBlueColor, "#1988e2")

  let anchorButtonColor = bannerData?.anchorButtonColor
  if (!anchorButtonColor?.startsWith("#")) {
    anchorButtonColor = "#" + anchorButtonColor
  }
  anchorButtonColor = verifyCtaColor(anchorButtonColor, "#ffffff")

  const textHTML = projectData?.textHTML
  const [isMediaReady, setIsMediaReady] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [hideTimerPopUp, setHideTimerPopUp] = useState<boolean>(false)
  // eslint-disable-next-line no-unused-vars
  const [hasScrolled, setHasScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(true)
      window.removeEventListener("scroll", handleScroll)
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const handleHide = () => {
    setHideTimerPopUp(true)
  }

  const handleLoadedMetadata: React.ReactEventHandler<HTMLVideoElement> = () => {
    setIsMediaReady(true)
    setIsLoading(false)
  }
  const handleProgress: React.ReactEventHandler<HTMLVideoElement> = () => {
    if (!isMediaReady) {
      setIsMediaReady(true)
      setIsLoading(false)
    }
  }
  const handleCanPlayThrough: React.ReactEventHandler<HTMLVideoElement> = () => {
    setIsMediaReady(true)
    setIsLoading(false)
  }
  const handleError: React.ReactEventHandler<HTMLVideoElement> = () => {
    setIsMediaReady(true)
    setIsLoading(false)
  }
  const { moreInfo } = projectData
  const addressParts = [country, city, moreInfo]
  const validAddressParts = addressParts.filter((part) => part !== undefined && part !== "")
  const addressString = validAddressParts.join(", ")

  // Helper to render media (image or video) based on file extension
  const renderMedia = (mediaObj: any, fallbackPoster?: string) => {
    if (!mediaObj?.url) {
      return null
    }
    const { url } = mediaObj
    const umbracoFriendlyUrl = getUmbracoMediaUrl(url)
    const ext = url.split(".").pop()?.toLowerCase()
    if (["mp4", "webm", "mov"].includes(ext)) {
      return (
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
          {isLoading && (
            <div
              style={{
                position: "absolute",
                zIndex: 2,
                ...(locale === "he"
                  ? {
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                    }
                  : {
                      top: "50%",
                      bottom: "50%",
                      transform: "translate(50%, 50%)",
                    }),
              }}
            >
              <CircularProgress />
            </div>
          )}
          <StyledVideo
            ready={isMediaReady && inView}
            src={umbracoFriendlyUrl}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster={fallbackPoster || mediaObj.poster || ""}
            onLoadedMetadata={handleLoadedMetadata}
            onProgress={handleProgress}
            onCanPlayThrough={handleCanPlayThrough}
            onError={handleError}
          />
        </div>
      )
    }
    // Use ImageWithAlt for images
    return (
      <StyledImage
        image={{ url: umbracoFriendlyUrl, altText: mediaObj.altText }}
        width={2000}
        height={2000}
        priority
        onLoad={() => {
          setIsMediaReady(true)
          setIsLoading(false)
        }}
      />
    )
  }

  // Choose desktop or mobile media based on breakpoint (simple example: always desktop)
  const desktopMedia = bannerData?.imageForDesktop?.[0]
  const mobileMedia = bannerData?.imageForMobile?.[0]

  // Helper to select which media to render based on device and available media
  const getHeroMediaToRender = () => {
    if (isDesktop) {
      if (desktopMedia) return desktopMedia
      if (mobileMedia) return mobileMedia
      return null
    } else {
      if (mobileMedia) return mobileMedia
      if (desktopMedia) return desktopMedia
      return null
    }
  }

  // If there is no media at all, immediately mark as ready
  useEffect(() => {
    if (!desktopMedia && !mobileMedia) {
      setIsMediaReady(true)
      setIsLoading(false)
    }
  }, [desktopMedia, mobileMedia])

  // Calculate left margin for skewed text content
  const textContentMargin = useTextContentMargin(SKEW_ANGLE, heroContentWrapperRef)

  // Extract target id from a URL/hash and smooth-scroll with offset (similar to TopHeader)
  const getIdFromUrl = (url?: string | null): string | null => {
    if (!url) return null
    if (url.startsWith("#")) return url.slice(1)
    try {
      const { href } =
        typeof window !== "undefined" ? window.location : { href: "http://localhost" }
      const { hash: urlHash } = new URL(url, href)
      if (urlHash) return urlHash.replace(/^#/, "")
    } catch {
      // ignore invalid URL
    }
    const idx = url.indexOf("#")
    return idx >= 0 ? url.slice(idx + 1) : null
  }

  const scrollToTarget = (
    blockPosition: ScrollLogicalPosition,
    url?: string | null,
    offset?: number,
  ) => {
    const id = getIdFromUrl(url)
    if (!id) return false
    const el = typeof document !== "undefined" ? document.getElementById(id) : null
    if (!el) return false

    if (offset) {
      window.scrollTo({ top: el.offsetTop + offset, behavior: "smooth" })
    }
    el.scrollIntoView({ behavior: "smooth", block: blockPosition || "end" })

    return true
  }

  const scrollToAnchorWithAnalytics = (
    blockPosition: ScrollLogicalPosition = "center",
    url?: string | null,
    label?: string | null,
    offset?: number,
  ) => {
    if (label) pageStripClick(label, "רכיב פתיחה דף פרוייקט")
    scrollToTarget(blockPosition, url, offset)
  }

  return (
    <>
      <OverflowClip ref={powerNumbersRef}>
        <StyledTransitionUp $inView={inView} as={"section"} $delay={transitionDelayTime} ref={ref}>
          <HeroContentWrapper ref={heroContentWrapperRef}>
            <HeroTextContentWrapper
              locale={locale}
              ready={isMediaReady && inView}
              textContentMargin={textContentMargin}
            >
              <HeroTextContentWrapperContent locale={locale}>
                <HeroTextTitle locale={locale}>
                  <span className="HeroTextTitle-default">{bannerData?.title} </span>
                  {bannerData?.titleWithBg && <strong>{bannerData?.titleWithBg}</strong>}
                </HeroTextTitle>
                <SlideFromSide ready={isMediaReady && inView}>
                  <StyledTextIcon>
                    <StyledIconButton
                      aria-label="Scroll to map"
                      title="Scroll to map"
                      onClick={() => {
                        window.scrollTo({
                          behavior: "smooth",
                          top:
                            document?.getElementById("mapContainer")?.getBoundingClientRect().top ||
                            0,
                        })
                      }}
                    >
                      <JobLocationIcon width={32} height={32} color={"currentColor"} />
                    </StyledIconButton>
                    <NewText
                      component="p"
                      desktopFontSize={32}
                      mobileFontSize={24}
                      desktopLineHeight="130%"
                      mobileLineHeight="20px"
                      color="inherit"
                      letterSpacing={0}
                      mobileFontWeight={400}
                      desktopFontWeight={400}
                      fontFamily={`var(--narkis) !important;`}
                    >
                      {addressString}
                    </NewText>
                  </StyledTextIcon>
                  {textHTML && (
                    <HeroDescription
                      dangerouslySetInnerHTML={{
                        __html: textHTML || "",
                      }}
                    />
                  )}
                  {(bannerData?.anchorButtonBlue?.[0]?.name ||
                    bannerData?.anchorButton?.[0]?.name) && (
                    <HeroActionButtons>
                      {bannerData?.anchorButtonBlue?.[0]?.url &&
                        bannerData?.anchorButtonBlue?.[0]?.name &&
                        getDynamicButton(
                          anchorButtonBlueColor,
                          {
                            onClick: () =>
                              scrollToAnchorWithAnalytics(
                                "end",
                                bannerData?.anchorButtonBlue?.[0]?.url,
                                bannerData?.anchorButtonBlue?.[0]?.name,
                              ),
                          },
                          bannerData?.anchorButtonBlue?.[0]?.name,
                        )}
                      {bannerData?.anchorButton?.[0]?.url &&
                        bannerData?.anchorButton?.[0]?.name &&
                        getDynamicButton(
                          anchorButtonColor,
                          {
                            onClick: () => {
                              if (hasScrolled) {
                                scheduleBmbyMeeting(locale)
                              } else {
                                scrollToAnchorWithAnalytics(
                                  "start",
                                  bannerData?.anchorButton?.[0]?.url,
                                  bannerData?.anchorButton?.[0]?.name,
                                  -280,
                                )
                              }
                            },
                          },
                          bannerData?.anchorButton?.[0]?.name,
                        )}
                    </HeroActionButtons>
                  )}
                  {bannerData?.disclaimer && (
                    <NewText
                      mt={{
                        xs: "-12px",
                        md: "32px",
                      }}
                      mb={{
                        xs: "12px",
                        md: "0px",
                      }}
                      component={"p"}
                      desktopFontSize={16}
                      mobileFontSize={14}
                      desktopFontWeight={400}
                      mobileFontWeight={400}
                      desktopLineHeight={"24px"}
                      mobileLineHeight={"21px"}
                      color={sharedColors.darkBlue4rentMarketing}
                    >
                      {bannerData?.disclaimer}
                    </NewText>
                  )}
                </SlideFromSide>
                {!isDesktop && allowPowerNumbers && powerNumbers && shouldRenderPowerNumbers && (
                  <PowerNumbers
                    shouldRenderTimer={false}
                    animFunction={anim({
                      duration: "800ms",
                      delay: parseInt(ANIM_DURATION_LATER_TEXT) + parseInt(ANIM_DURATION) + "ms",
                    })}
                    mobileAnimFunction={anim({
                      duration: "800ms",
                      delay:
                        parseInt(ANIM_DURATION_LATER_TEXT_MOBILE) +
                        parseInt(ANIM_DURATION_MOBILE) +
                        "ms",
                    })}
                    powerNumbers={powerNumbers}
                    ready={isMediaReady && inView}
                    locale={locale}
                    secondAsInteger
                  />
                )}
              </HeroTextContentWrapperContent>
            </HeroTextContentWrapper>
            <HeroMediaContainer
              locale={locale}
              ready={isMediaReady && inView}
              style={{
                insetInlineEnd: 0,
              }}
            >
              {/* Improved: use helper to select which media to render */}
              {getHeroMediaToRender() && renderMedia(getHeroMediaToRender())}
            </HeroMediaContainer>
            {isDesktop &&
              allowPowerNumbers &&
              (shouldRenderPowerNumbers || !!rentProjectFOMO?.timer) && (
                <PowerNumbers
                  shouldRenderTimer={!!rentProjectFOMO?.timer}
                  animFunction={anim({
                    duration: "800ms",
                    delay: parseInt(ANIM_DURATION_LATER_TEXT) + parseInt(ANIM_DURATION) + "ms",
                  })}
                  mobileAnimFunction={anim({
                    duration: "800ms",
                    delay:
                      parseInt(ANIM_DURATION_LATER_TEXT_MOBILE) +
                      parseInt(ANIM_DURATION_MOBILE) +
                      "ms",
                  })}
                  rentProjectFOMO={rentProjectFOMO}
                  powerNumbers={powerNumbers}
                  locale={locale}
                  ready={isMediaReady && inView}
                  secondAsInteger
                />
              )}
          </HeroContentWrapper>
        </StyledTransitionUp>
        {!isDesktop && rentProjectFOMO?.timer && !isLoading && !hideTimerPopUp && (
          <TimerPopup locale={locale} rentProjectFOMO={rentProjectFOMO} handleHide={handleHide} />
        )}
      </OverflowClip>
      {isDesktop && rentProjectFOMO?.timer && !hideTimerPopUp && inView && !powerNumbersInView && (
        <TimerPopup
          locale={locale}
          rentProjectFOMO={rentProjectFOMO}
          handleHide={handleHide}
          forDesktop
        />
      )}
    </>
  )
}
export default HeroContent
